import { supabase } from '../lib/supabase'
import type { Customer, Table, Shift, Reservation } from '../types'
import * as Sentry from '@sentry/react'

const requireTenant = (tenantId?: string) => {
  if (!tenantId) {
    throw new Error('tenantId is required for multi-tenant Supabase operations');
  }
  return tenantId;
};

// Define WaitlistEntry locally or import if available in types
export interface WaitlistEntry {
  id: string;
  customer_id: string;
  date: string;
  time: string;
  party_size: number;
  priority: number;
  status: 'waiting' | 'contacted' | 'confirmed' | 'cancelled';
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface TableGroupRecord {
  id: string;
  tenant_id: string;
  anchor_table_id: string;
  table_ids: string[];
  room_id?: string | null;
  label?: string | null;
  status: 'active' | 'split';
  created_at: string;
  updated_at: string;
}

// Customer Service
export const customerService = {
  // Get all customers with pagination
  async getCustomers(tenantId: string, page = 1, limit = 20, search = '') {
    requireTenant(tenantId);
    let query = supabase
      .from('customers_with_stats')
      .select('*', { count: 'exact' })
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })

    if (search) {
      query = query.or(`name.ilike.%${search}%,phone.ilike.%${search}%`)
    }

    const { data, error, count } = await query
      .range((page - 1) * limit, page * limit - 1)
      .limit(limit)

    if (error) throw error
    return { data: data || [], total: count || 0 }
  },

  /** All rows from `customers_with_stats` for the tenant (paged fetch for large directories). */
  async listAllCustomersWithStats(tenantId: string) {
    requireTenant(tenantId)
    const pageSize = 1000
    const all: Record<string, unknown>[] = []
    let from = 0
    for (;;) {
      const { data, error } = await supabase
        .from('customers_with_stats')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .range(from, from + pageSize - 1)
      if (error) throw error
      const chunk = data || []
      if (chunk.length === 0) break
      all.push(...chunk)
      if (chunk.length < pageSize) break
      from += pageSize
    }
    return all
  },

  async listAllCustomers(tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('tenant_id', tenantId)

    if (error) throw error
    return data || []
  },

  async upsertCustomerRecord(customer: Partial<Customer> & { id: string; tenant_id?: string }) {
    const tenantId = requireTenant(customer.tenant_id);
    const { id, name, phone, notes, created_at, updated_at } = customer
    const sanitizedPayload = {
      id,
      tenant_id: tenantId,
      name: name ?? null,
      phone: phone ?? null,
      notes: notes ?? null,
      created_at: created_at ?? new Date().toISOString(),
      updated_at: updated_at ?? new Date().toISOString(),
    }

    const { data, error } = await supabase
      .from('customers')
      .upsert(sanitizedPayload, { onConflict: 'id' })
      .select('id, name, phone, notes, created_at, updated_at')
      .single()

    if (error) throw error
    return data
  },

  async deleteCustomerRecord(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { error } = await supabase
      .from('customers')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId)

    if (error) throw error
  },

  // Get customer by ID
  async getCustomer(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .maybeSingle() // avoid 406 when missing; return null

    if (error) throw error
    return data
  },

  // Create customer
  async createCustomer(customer: Omit<Customer, 'id' | 'created_at' | 'updated_at' | 'tenant_id'> & { tenant_id: string }) {
    const tenantId = requireTenant(customer.tenant_id);
    const { data, error } = await supabase
      .from('customers')
      .insert([{ ...customer, tenant_id: tenantId }])
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Update customer
  async updateCustomer(id: string, updates: Partial<Customer> & { tenant_id: string }) {
    const tenantId = requireTenant(updates.tenant_id);
    const { data, error } = await supabase
      .from('customers')
      .update(updates)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Delete customer
  async deleteCustomer(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { error } = await supabase
      .from('customers')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId)

    if (error) throw error
  },

  // Get customer by phone
  async getCustomerByPhone(phone: string, tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('phone', phone)
      .eq('tenant_id', tenantId)
      .order('updated_at', { ascending: false }) // Prefer the most recent record if duplicates exist
      .limit(1)
      .maybeSingle(); // Avoid 406 when 0 rows; returns null instead

    if (error) throw error;
    return data ?? null;
  }
}

// Table Service
export const tableService = {
  // Get all tables
  async getTables(tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('tables')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('number', { ascending: true })

    if (error) throw error
    return data || []
  },

  async listAllTables(tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('tables')
      .select('*')
      .eq('tenant_id', tenantId)

    if (error) throw error
    return data || []
  },

  async upsertTableRecord(table: Partial<Table> & { id: string; tenant_id?: string }) {
    const tenantId = requireTenant(table.tenant_id);
    const { data, error } = await supabase
      .from('tables')
      .upsert({ ...table, tenant_id: tenantId }, { onConflict: 'id' })
      .select('*')
      .single()

    if (error) throw error
    return data
  },

  async deleteTableRecord(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { error } = await supabase
      .from('tables')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId)

    if (error) throw error
  },

  // Get table by ID
  async getTable(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('tables')
      .select('*')
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .maybeSingle() // avoid 406 when missing; return null

    if (error) throw error
    return data
  },

  // Update table
  async updateTable(id: string, updates: Partial<Table> & { tenant_id: string }) {
    const tenantId = requireTenant(updates.tenant_id);
    const { data, error } = await supabase
      .from('tables')
      .update(updates)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Get table availability for date, shift, and optionally a specific time
  async getTableAvailability(_date: string, _shiftId: string, _time?: string) {
    throw new Error('getTableAvailability requires tenantId. Use getTableAvailabilityForTenant instead.');
  },

  async getTableAvailabilityForTenant(tenantId: string, date: string, shiftId: string, time?: string) {
    requireTenant(tenantId);
    const params: Record<string, any> = { 
      p_tenant_id: tenantId,
      p_date: date, 
      p_shift: shiftId,
      p_duration_minutes: 125 // 2 hours + 5 min buffer
    }
    
    // If time is provided, check for time-slot specific availability
    if (time) {
      params.p_time = time
    }
    
    const { data, error } = await supabase.rpc('available_tables_for_shift', params)

    if (error) throw error
    return data || []
  },

  async getTableByNumber(_number: number) {
    throw new Error('getTableByNumber requires tenantId. Use getTableByNumberForTenant instead.');
  },

  async getTableByNumberForTenant(number: number, tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('tables')
      .select('*')
      .eq('number', number)
      .eq('tenant_id', tenantId)
      .maybeSingle() // avoid 406 when no rows

    if (error) throw error
    return data
  }
}

export const tableGroupService = {
  async listAllTableGroups(tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('table_groups')
      .select('*')
      .eq('tenant_id', tenantId);

    if (error) throw error;
    return (data || []) as TableGroupRecord[];
  },

  async upsertTableGroupRecord(group: Partial<TableGroupRecord> & { id: string; tenant_id?: string }) {
    const tenantId = requireTenant(group.tenant_id);
    const { data, error } = await supabase
      .from('table_groups')
      .upsert({ ...group, tenant_id: tenantId }, { onConflict: 'id' })
      .select('*')
      .single();

    if (error) throw error;
    return data as TableGroupRecord;
  },

  async deleteTableGroupRecord(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { error } = await supabase
      .from('table_groups')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId);
    if (error) throw error;
  },
};

// Shift Service
export const shiftService = {
  // Get all shifts
  async getShifts(tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('shifts')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .order('start_time', { ascending: true })

    if (error) throw error
    return data || []
  },

  async listAllShifts(tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('shifts')
      .select('*')
      .eq('tenant_id', tenantId)

    if (error) throw error
    return data || []
  },

  async upsertShiftRecord(shift: Partial<Shift> & { id: string; tenant_id?: string }) {
    const tenantId = requireTenant(shift.tenant_id);
    const { data, error } = await supabase
      .from('shifts')
      .upsert({ ...shift, tenant_id: tenantId }, { onConflict: 'id' })
      .select('*')
      .single()

    if (error) throw error
    return data
  },

  async deleteShiftRecord(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { error } = await supabase
      .from('shifts')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId)

    if (error) throw error
  },

  // Get shift by ID
  async getShift(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('shifts')
      .select('*')
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .maybeSingle() // avoid 406 when no rows

    if (error) throw error
    return data
  }
}

// Reservation Service
type ReservationWithAssignments = Partial<Reservation> & {
  id: string;
  table_id?: string | null;
  table_ids?: string[];
  tables?: Array<Pick<Table, 'id' | 'number' | 'seats' | 'zone'>>;
};

type ReservationTableAssignmentRow = {
  reservation_id: string;
  table_id: string;
  sort_index: number;
  tables?: Pick<Table, 'id' | 'number' | 'seats' | 'zone'> | Array<Pick<Table, 'id' | 'number' | 'seats' | 'zone'>> | null;
};

const normalizeReservationTableIds = (
  tableIds?: string[] | null,
  tableId?: string | null,
): string[] => {
  if (Array.isArray(tableIds) && tableIds.length > 0) {
    return Array.from(new Set(tableIds.filter(Boolean)));
  }
  return tableId ? [tableId] : [];
};

async function fetchReservationAssignments(
  tenantId: string,
  reservationIds: string[],
): Promise<Map<string, ReservationTableAssignmentRow[]>> {
  if (reservationIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from('reservation_tables')
    .select('reservation_id, table_id, sort_index, tables:table_id(id, number, seats, zone)')
    .eq('tenant_id', tenantId)
    .in('reservation_id', reservationIds)
    .order('sort_index', { ascending: true });

  if (error) throw error;

  const assignmentMap = new Map<string, ReservationTableAssignmentRow[]>();
  (data || []).forEach((row) => {
    const list = assignmentMap.get(row.reservation_id) || [];
    list.push(row as unknown as ReservationTableAssignmentRow);
    assignmentMap.set(row.reservation_id, list);
  });

  return assignmentMap;
}

function mergeReservationAssignments<T extends ReservationWithAssignments>(
  reservations: T[],
  assignmentMap: Map<string, ReservationTableAssignmentRow[]>,
): T[] {
  return reservations.map((reservation) => {
    const assignments = assignmentMap.get(reservation.id) || [];
    const tableIds = assignments.length
      ? assignments.map((assignment) => assignment.table_id)
      : normalizeReservationTableIds(reservation.table_ids, reservation.table_id);
    const nestedTables = assignments
      .map((assignment) => Array.isArray(assignment.tables) ? assignment.tables[0] : assignment.tables)
      .filter(Boolean) as Array<Pick<Table, 'id' | 'number' | 'seats' | 'zone'>>;

    return {
      ...reservation,
      table_id: reservation.table_id ?? tableIds[0] ?? null,
      table_ids: tableIds,
      tables: nestedTables.length > 0 ? nestedTables : reservation.tables,
    };
  });
}

async function setReservationTableAssignments(
  tenantId: string,
  reservationId: string,
  tableIds: string[],
): Promise<void> {
  const normalizedTableIds = Array.from(new Set(tableIds.filter(Boolean)));

  const { error: deleteError } = await supabase
    .from('reservation_tables')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('reservation_id', reservationId);

  if (deleteError) throw deleteError;

  if (normalizedTableIds.length === 0) return;

  const { error: insertError } = await supabase
    .from('reservation_tables')
    .insert(
      normalizedTableIds.map((tableId, index) => ({
        tenant_id: tenantId,
        reservation_id: reservationId,
        table_id: tableId,
        sort_index: index,
      })),
    );

  if (insertError) throw insertError;
}

async function loadReservationWithAssignments(tenantId: string, reservationId: string) {
  const { data, error } = await supabase
    .from('reservations')
    .select(`
      *,
      customers:customer_id(name, phone),
      tables:table_id(id, number, seats, zone),
      shifts:shift_id(name, start_time, end_time)
    `)
    .eq('tenant_id', tenantId)
    .eq('id', reservationId)
    .single();

  if (error) throw error;
  const assignmentMap = await fetchReservationAssignments(tenantId, [reservationId]);
  return mergeReservationAssignments([data as ReservationWithAssignments], assignmentMap)[0];
}

export const reservationService = {
  // Get reservations for date and shift
  async getReservations(tenantId: string, date: string, shiftId?: string) {
    requireTenant(tenantId);
    let query = supabase
      .from('reservations')
      .select(`
        *,
        customers:customer_id(name, phone),
        tables:table_id(number, seats, zone),
        shifts:shift_id(name, start_time, end_time)
      `)
      .eq('tenant_id', tenantId)
      .eq('date', date)
      .order('time', { ascending: true })

    if (shiftId) {
      query = query.eq('shift_id', shiftId)
    }

    const { data, error } = await query

    if (error) throw error
    const reservations = (data || []) as ReservationWithAssignments[]
    const assignmentMap = await fetchReservationAssignments(tenantId, reservations.map((r) => r.id))
    return mergeReservationAssignments(reservations, assignmentMap)
  },

  async listAllReservations(tenantId: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('reservations')
      .select('*')
      .eq('tenant_id', tenantId)

    if (error) throw error
    const reservations = (data || []) as ReservationWithAssignments[]
    const assignmentMap = await fetchReservationAssignments(tenantId, reservations.map((r) => r.id))
    return mergeReservationAssignments(reservations, assignmentMap)
  },

  async upsertReservationRecord(reservation: Partial<Reservation> & { id: string; tenant_id?: string }) {
    const tenantId = requireTenant(reservation.tenant_id);
    const { table_ids, tables: _tables, ...reservationPayload } = reservation as ReservationWithAssignments;
    const normalizedTableIds = normalizeReservationTableIds(table_ids, reservationPayload.table_id);
    const { data, error } = await supabase
      .from('reservations')
      .upsert({ ...reservationPayload, table_id: normalizedTableIds[0] ?? null, tenant_id: tenantId }, { onConflict: 'id' })
      .select('*')
      .single()

    if (error) throw error
    await setReservationTableAssignments(tenantId, data.id, normalizedTableIds)
    return loadReservationWithAssignments(tenantId, data.id)
  },

  // Simple insert without auto-assignment logic (for sync pipeline); preserves caller-provided id
  async insertReservationRecord(reservation: Partial<Reservation> & { id: string; tenant_id?: string }) {
    const tenantId = requireTenant(reservation.tenant_id);
    const { table_ids, tables: _tables, ...reservationPayload } = reservation as ReservationWithAssignments;
    const normalizedTableIds = normalizeReservationTableIds(table_ids, reservationPayload.table_id);
    // Use upsert on id to make the operation idempotent for sync retries
    const { data, error } = await supabase
      .from('reservations')
      .upsert({ ...reservationPayload, table_id: normalizedTableIds[0] ?? null, tenant_id: tenantId }, { onConflict: 'id' })
      .select(`
        *,
        customers:customer_id(name, phone),
        tables:table_id(id, number, seats, zone),
        shifts:shift_id(name, start_time, end_time)
      `)
      .single()

    if (error) throw error
    await setReservationTableAssignments(tenantId, data.id, normalizedTableIds)
    return loadReservationWithAssignments(tenantId, data.id)
  },

  async deleteReservationRecord(id: string, tenantId: string) {
    requireTenant(tenantId);
    const { error } = await supabase
      .from('reservations')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId)

    if (error) throw error
  },

  // Create reservation
  async createReservation(reservation: Omit<Reservation, 'id' | 'created_at' | 'updated_at' | 'tenant_id'> & { tenant_id: string }) {
    try {
      const { date, time, shift_id, table_id, party_size, tenant_id, table_ids, tables: _tables, ...restReservation } = reservation as any
      const tenantId = requireTenant(tenant_id);

      // If a table is specified, validate conflict
      if (table_id) {
        const { data: hasConflict, error: conflictErr } = await supabase.rpc('check_table_conflict', {
          p_tenant_id: tenantId, // required by RPC signature
          p_table_id: table_id,
          p_date: date,
          p_time: time,
          p_shift: shift_id,
          p_duration_minutes: 125, // 2 hours (120 min) + 5 min cleanup buffer
          p_exclude_reservation: null
        })
        if (conflictErr) throw conflictErr
        if (hasConflict) {
          throw new Error('Η ώρα είναι δεσμευμένη για αυτό το τραπέζι (σύγκρουση κρατήσεων).')
        }
      }

      // If no table specified, auto-assign the smallest suitable available table
      let assignedTableId = table_id as string | undefined
      if (!assignedTableId) {
        const { data: availableTables, error: availErr } = await supabase.rpc('available_tables_for_shift', {
          p_tenant_id: tenantId,
          p_date: date,
          p_shift: shift_id,
          p_time: time, // Pass the time to check for time-slot specific availability
          p_duration_minutes: 125 // 2 hours + 5 min buffer
        })
        if (availErr) throw availErr
        const candidate = (availableTables || [])
          .filter((t: Table) => t.seats >= party_size)
          .sort((a: Table, b: Table) => a.seats - b.seats)[0]
        if (candidate) {
          assignedTableId = candidate.id
        }
      }

      const normalizedTableIds = normalizeReservationTableIds(table_ids, assignedTableId)
      const insertPayload = { ...restReservation, table_id: normalizedTableIds[0] ?? null }

        const { data, error } = await supabase
          .from('reservations')
          .insert([{ ...insertPayload, tenant_id: tenantId }])
        .select(`
          *,
          customers:customer_id(name, phone),
          tables:table_id(id, number, seats, zone),
          shifts:shift_id(name, start_time, end_time)
        `)
        .single()

      if (error) throw error
      await setReservationTableAssignments(tenantId, data.id, normalizedTableIds)
      return loadReservationWithAssignments(tenantId, data.id)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'create_reservation', severity: 'high' },
        contexts: {
          reservation: {
            date: (reservation as any).date,
            time: (reservation as any).time,
            party_size: (reservation as any).party_size
          }
        }
      })
      throw error
    }
  },

  // Update reservation
  async updateReservation(id: string, updates: Partial<Reservation> & { tenant_id: string }) {
    try {
      const { date, time, shift_id, table_id, tenant_id, table_ids, tables: _tables, ...restUpdates } = updates as any
      const tenantId = requireTenant(tenant_id);

      // If updating to a specific table/time, validate conflict excluding current reservation
      if (date && time && shift_id && table_id) {
        const { data: hasConflict, error: conflictErr } = await supabase.rpc('check_table_conflict', {
          p_tenant_id: tenantId,
          p_table_id: table_id,
          p_date: date,
          p_time: time,
          p_shift: shift_id,
          p_duration_minutes: 125, // 2 hours (120 min) + 5 min cleanup buffer
          p_exclude_reservation: id
        })
        if (conflictErr) throw conflictErr
        if (hasConflict) {
          throw new Error('Η ώρα είναι δεσμευμένη για αυτό το τραπέζι (σύγκρουση κρατήσεων).')
        }
      }

      const normalizedTableIds = normalizeReservationTableIds(
        Array.isArray(table_ids) ? table_ids : undefined,
        table_id,
      )

      const { data, error } = await supabase
        .from('reservations')
        .update({ ...restUpdates, table_id: normalizedTableIds[0] ?? null })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select(`
          *,
          customers:customer_id(name, phone),
          tables:table_id(id, number, seats, zone),
          shifts:shift_id(name, start_time, end_time)
        `)
        .single()

      if (error) throw error
      await setReservationTableAssignments(tenantId, data.id, normalizedTableIds)
      return loadReservationWithAssignments(tenantId, data.id)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'update_reservation', severity: 'high' },
        contexts: {
          reservation: {
            id,
            updates: {
              date: (updates as any).date,
              time: (updates as any).time,
              status: (updates as any).status
            }
          }
        }
      })
      throw error
    }
  },

  // Delete reservation
  async deleteReservation(id: string, tenantId: string) {
    try {
      requireTenant(tenantId);
      const { error } = await supabase
        .from('reservations')
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId)

      if (error) throw error
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'delete_reservation', severity: 'high' },
        contexts: {
          reservation: { id, tenantId }
        }
      })
      throw error
    }
  },

  // Check for conflicts
  async checkConflicts(tenantId: string, date: string, time: string, shiftId: string, tableId?: string, excludeId?: string) {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('reservations')
      .select('id, table_id')
      .eq('tenant_id', tenantId)
      .eq('date', date)
      .eq('time', time)
      .eq('shift_id', shiftId)
      .in('status', ['confirmed', 'in-progress'])

    if (error) throw error
    const reservations = (data || []) as ReservationWithAssignments[]
    const assignmentMap = await fetchReservationAssignments(tenantId, reservations.map((reservation) => reservation.id))
    return mergeReservationAssignments(reservations, assignmentMap).filter((reservation: ReservationWithAssignments) => {
      if (excludeId && reservation.id === excludeId) return false
      if (!tableId) return true
      return normalizeReservationTableIds(reservation.table_ids, reservation.table_id).includes(tableId)
    })
  }
}

// Tenant Service (for owner settings, e.g. online booking table limit)
export interface TenantRow {
  id: string;
  name: string;
  slug: string | null;
  created_at: string;
  online_booking_capacity_percent: number | null;
  max_online_party_size: number | null;
}

export const tenantService = {
  async getTenant(tenantId: string): Promise<TenantRow | null> {
    requireTenant(tenantId);
    const { data, error} = await supabase
      .from('tenants')
      .select('id, name, slug, created_at, online_booking_capacity_percent, max_online_party_size')
      .eq('id', tenantId)
      .maybeSingle();
    if (error) {
      console.error('getTenant error:', error);
      throw error;
    }
    return data;
  },

  async updateTenant(
    tenantId: string,
    updates: {
      online_booking_capacity_percent?: number | null;
      max_online_party_size?: number | null;
    }
  ): Promise<TenantRow> {
    requireTenant(tenantId);
    const { data, error } = await supabase
      .from('tenants')
      .update(updates)
      .eq('id', tenantId)
      .select('id, name, slug, created_at, online_booking_capacity_percent, max_online_party_size')
      .single();
    if (error) {
      console.error('updateTenant error:', error);
      throw error;
    }
    return data;
  },
};

// Waitlist Service
export const waitlistService = {
  // Get waitlist entries
  async getWaitlist(date: string, shiftId?: string) {
    let query = supabase
      .from('waitlist')
      .select(`
        *,
        customers:customer_id(name, phone)
      `)
      .eq('date', date)
      .eq('status', 'waiting')
      .order('priority', { ascending: true })
      .order('created_at', { ascending: true })

    if (shiftId) {
      query = query.eq('shift_id', shiftId)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  },

  // Add to waitlist
  async addToWaitlist(entry: Omit<WaitlistEntry, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('waitlist')
      .insert([entry])
      .select(`
        *,
        customers:customer_id(name, phone)
      `)
      .single()

    if (error) throw error
    return data
  },

  // Update waitlist entry
  async updateWaitlistEntry(id: string, updates: Partial<WaitlistEntry>) {
    const { data, error } = await supabase
      .from('waitlist')
      .update(updates)
      .eq('id', id)
      .select(`
        *,
        customers:customer_id(name, phone)
      `)
      .single()

    if (error) throw error
    return data
  },

  // Remove from waitlist
  async removeFromWaitlist(id: string) {
    const { error } = await supabase
      .from('waitlist')
      .delete()
      .eq('id', id)

    if (error) throw error
  }
}

// Real-time subscriptions
export const realtimeService = {
  // Subscribe to reservation changes
  subscribeToReservations(callback: (payload: any) => void) {
    return supabase
      .channel('reservations')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'reservations' }, 
        callback
      )
      .subscribe()
  },

  // Subscribe to customer changes
  subscribeToCustomers(callback: (payload: any) => void) {
    return supabase
      .channel('customers')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'customers' }, 
        callback
      )
      .subscribe()
  },

  // Subscribe to table changes
  subscribeToTables(callback: (payload: any) => void) {
    return supabase
      .channel('tables')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'tables' }, 
        callback
      )
      .subscribe()
  },

  // Unsubscribe from channel
  unsubscribe(channel: any) {
    return supabase.removeChannel(channel)
  }
}

// Floor Plan Room Settings Service
export interface FloorPlanRoomSettings {
  id: string;
  tenant_id: string;
  room_id: string;
  label: string | null;
  grid_width: number;
  grid_height: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const floorPlanRoomService = {
  /** Check if the table exists (migration applied) */
  _tableExists: null as boolean | null,

  _isMissingTableError(error: any): boolean {
    if (!error) return false;
    const code = String(error.code || '');
    const message = String(error.message || '').toLowerCase();
    const status = Number(error.status || 0);
    return (
      code === '42P01' ||
      code === 'PGRST205' ||
      status === 404 ||
      message.includes('does not exist') ||
      message.includes('could not find the table')
    );
  },

  async _checkTableExists(forceRefresh = false): Promise<boolean> {
    if (!forceRefresh && this._tableExists !== null) return this._tableExists;
    try {
      const { error } = await supabase.from('floor_plan_rooms').select('id').limit(1);
      if (error) {
        this._tableExists = !this._isMissingTableError(error);
      } else {
        this._tableExists = true;
      }
      return this._tableExists;
    } catch {
      this._tableExists = false;
      return false;
    }
  },

  /** Get all room settings for a tenant */
  async getRoomSettings(tenantId: string): Promise<FloorPlanRoomSettings[]> {
    requireTenant(tenantId);
    
    // Check if table exists first
    const tableExists = await this._checkTableExists();
    if (!tableExists) {
      console.info('[floorPlanRoomService] floor_plan_rooms table not yet created. Using defaults.');
      return [];
    }

    const { data, error } = await supabase
      .from('floor_plan_rooms')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('sort_order', { ascending: true });
    
    if (error) {
      if (this._isMissingTableError(error)) {
        this._tableExists = false;
        return [];
      }
      console.error('getRoomSettings error:', error);
      return [];
    }
    return data || [];
  },

  /** Get settings for a specific room */
  async getRoomSettingsByRoomId(tenantId: string, roomId: string): Promise<FloorPlanRoomSettings | null> {
    requireTenant(tenantId);
    
    const tableExists = await this._checkTableExists();
    if (!tableExists) return null;

    const { data, error } = await supabase
      .from('floor_plan_rooms')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('room_id', roomId)
      .maybeSingle();
    
    if (error) {
      if (this._isMissingTableError(error)) {
        this._tableExists = false;
        return null;
      }
      console.error('getRoomSettingsByRoomId error:', error);
      return null;
    }
    return data;
  },

  /** Create or update room settings (upsert) */
  async upsertRoomSettings(
    tenantId: string,
    roomId: string,
    settings: { label?: string; grid_width?: number; grid_height?: number; sort_order?: number }
  ): Promise<FloorPlanRoomSettings | null> {
    requireTenant(tenantId);
    
    // Force refresh here so a table created after app start is detected immediately.
    const tableExists = await this._checkTableExists(true);
    if (!tableExists) {
      console.warn('[floorPlanRoomService] Cannot save room settings - table not created. Please run migration: 20260328_add_floor_plan_rooms.sql');
      return null;
    }

    const { data, error } = await supabase
      .from('floor_plan_rooms')
      .upsert(
        {
          tenant_id: tenantId,
          room_id: roomId,
          ...settings,
        },
        { onConflict: 'tenant_id,room_id' }
      )
      .select('*')
      .single();
    
    if (error) {
      if (this._isMissingTableError(error)) {
        this._tableExists = false;
        console.warn('[floorPlanRoomService] floor_plan_rooms table missing. Run migration 20260328_add_floor_plan_rooms.sql');
        return null;
      }
      console.error('upsertRoomSettings error:', error);
      return null;
    }
    return data;
  },

  /** Update grid dimensions for a room */
  async updateGridDimensions(
    tenantId: string,
    roomId: string,
    gridWidth: number,
    gridHeight: number
  ): Promise<FloorPlanRoomSettings | null> {
    return this.upsertRoomSettings(tenantId, roomId, {
      grid_width: gridWidth,
      grid_height: gridHeight,
    });
  },

  /** Subscribe to room settings changes for real-time sync */
  subscribeToRoomSettings(tenantId: string, callback: (payload: any) => void) {
    if (this._tableExists === false) {
      return { unsubscribe: () => {} } as any;
    }
    return supabase
      .channel('floor_plan_rooms')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'floor_plan_rooms', filter: `tenant_id=eq.${tenantId}` },
        callback
      )
      .subscribe();
  },
};
